import { useState, useMemo, useDeferredValue, useEffect, ChangeEvent, useCallback } from "react";
import { ChevronLeft, ChevronRight, Plus, Trash2 } from "lucide-react";
import { Button } from "@libs/zapi-ui/ds/components/button";
import { Checkbox } from "@libs/zapi-ui/ds/components/checkbox";
import { SearchBar, SearchBarIcon, SearchBarInput } from "@libs/zapi-ui/ds/components/search-bar";
import { Spinner } from "@libs/zapi-ui/ds/components/spinner";
import { Table, TableBody, TableHead, TableHeader, TableRow } from "@libs/zapi-ui/ds/components/table";
import { InviteUserModal } from "./InviteUserModal";
import { ManageAccessDrawer } from "./ManageAccessDrawer";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { getOrganizationData } from "../services/getOrganizationData";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { useOrganizationStore } from "@/zapi-frontend/stores/organization";
import { isSystemAdmin } from "@/zapi-frontend/shared/utils/permissions";
import { getRouteProjects } from "@/zapi-frontend/shared/const/router";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { TGetUsersResponse, TUser } from "../services/schemas";
import { TeamTableRow } from "./TeamTableRow";

const PAGE_SIZE = 10;

function pageNumbers(current: number, total: number): (number | "gap")[] {
  if (total <= 7) return Array.from({ length: total }, (_, index) => index + 1);
  const around = [current - 1, current, current + 1].filter((page) => page > 1 && page < total);
  const pages: (number | "gap")[] = [1];
  if (around[0] > 2) pages.push("gap");
  pages.push(...around);
  if (around[around.length - 1] < total - 1) pages.push("gap");
  pages.push(total);
  return pages;
}

export function TeamTable() {
  const { t } = useTranslation(["settings", "common"]);
  const [searchQuery, setSearchQuery] = useState("");
  const deferredSearchQuery = useDeferredValue(searchQuery);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isAccessDrawerOpen, setIsAccessDrawerOpen] = useState(false);
  const [selectedUserForAccess, setSelectedUserForAccess] = useState<TUser | null>(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { organizationId: defaultOrganizationId, id: currentUserId, role } = useMeStore();
  const { currentOrganizationId } = useOrganizationStore();

  const organizationId =
    isSystemAdmin(role) && currentOrganizationId > 0 ? currentOrganizationId : defaultOrganizationId;

  const { data: organizationData, isLoading: isLoadingOrganizationData } = useQuery<TGetUsersResponse, Error>({
    queryKey: ["organizationData", organizationId],
    queryFn: async () => {
      if (!organizationId) return [];
      try {
        const response = await getOrganizationData(organizationId);
        return response.map((user) => ({ ...user, createdAt: new Date(user.createdAt) }));
      } catch (error) {
        console.error(error);
        toast.error(t("teamTable.fetchFailed"));
        navigate(getRouteProjects());
        throw error;
      }
    },
    enabled: !!organizationId
  });

  const filteredTeamMembers = useMemo(() => {
    if (!organizationData) return [];
    if (!deferredSearchQuery) return organizationData;
    return organizationData.filter(
      (member) =>
        member.firstName.toLowerCase().startsWith(deferredSearchQuery.toLowerCase()) ||
        member.lastName.toLowerCase().startsWith(deferredSearchQuery.toLowerCase()) ||
        member.email.toLowerCase().startsWith(deferredSearchQuery.toLowerCase())
    );
  }, [deferredSearchQuery, organizationData]);

  useEffect(() => {
    setCurrentPage(1);
  }, [deferredSearchQuery]);

  const totalPages = Math.max(1, Math.ceil(filteredTeamMembers.length / PAGE_SIZE));
  const pageMembers = useMemo(
    () => filteredTeamMembers.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [filteredTeamMembers, currentPage]
  );

  const displayedMemberIds = useMemo(() => pageMembers.map((member) => member.id), [pageMembers]);

  const handleSelectAll = (checked: boolean | "indeterminate") => {
    if (checked === true) setSelectedRows(displayedMemberIds);
    else setSelectedRows([]);
  };

  const handleSelectRow = useCallback((id: number) => {
    setSelectedRows((prev) => (prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]));
  }, []);

  const handleRemoveSelectedUsers = () => {
    toast.success(`${selectedRows.length} user(s) submitted for removal.`);
    setSelectedRows([]);
  };

  const handleOpenManageAccess = useCallback((user: TUser) => {
    setSelectedUserForAccess(user);
    setIsAccessDrawerOpen(true);
  }, []);

  const handleDrawerClose = (openState: boolean) => {
    setIsAccessDrawerOpen(openState);
    if (!openState) {
      setSelectedUserForAccess(null);
    }
  };

  const isAllSelected = displayedMemberIds.length > 0 && selectedRows.length === displayedMemberIds.length;

  if (isLoadingOrganizationData || !organizationId) {
    return (
      <Spinner aria-label={t("common:loading")} className="size-5 text-muted-foreground" />
    );
  }

  return (
    <div className="w-full space-y-4">
      <div>
        <h1 className="typo-h5">{t("teamTable.heading")}</h1>
      </div>
      <div className="flex items-center justify-between">
        <div className="relative flex flex-1 flex-col gap-2">
          <SearchBar className="w-[320px] ps-8">
            <SearchBarIcon />
            <SearchBarInput
              data-testid="team-table-search-input"
              placeholder={t("teamTable.searchPlaceholder")}
              value={searchQuery}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
            />
          </SearchBar>
        </div>
        <div className="flex items-center space-x-3">
          {selectedRows.length > 0 ? (
            <>
              <span className="text-[14px] font-medium text-foreground">
                {t("teamTable.usersSelected", { count: selectedRows.length })}
              </span>
              <Button
                type="button"
                variant="secondary"
                data-testid="team-table-remove-selected-button"
                onClick={handleRemoveSelectedUsers}
              >
                <Trash2 className="h-4 w-4" />
                {t("teamTable.remove")}
              </Button>
            </>
          ) : (
            <Button
              type="button"
              data-testid="team-table-invite-user-button"
              onClick={() => setIsInviteModalOpen(true)}
            >
              <Plus className="h-4 w-4" />
              {t("teamTable.user")}
            </Button>
          )}
        </div>
      </div>
      <div className="min-h-[calc(100vh-300px)] w-full overflow-auto rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[50px]">
                <Checkbox
                  checked={isAllSelected}
                  onCheckedChange={handleSelectAll}
                  aria-label={t("teamTable.selectAll")}
                  disabled
                />
              </TableHead>
              <TableHead className="w-[250px] normal-case">{t("teamTable.user")}</TableHead>
              <TableHead className="normal-case">{t("teamTable.role")}</TableHead>
              <TableHead className="normal-case">{t("teamTable.projects")}</TableHead>
              <TableHead className="normal-case">{t("teamTable.accountStatus")}</TableHead>
              <TableHead className="normal-case">{t("teamTable.dateAdded")}</TableHead>
              <TableHead className="w-[150px] normal-case">{t("teamTable.actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pageMembers.map((member) => (
              <TeamTableRow
                key={member.id}
                member={member}
                organizationId={organizationId}
                currentUserId={currentUserId}
                selectedRows={selectedRows}
                handleSelectRow={handleSelectRow}
                handleOpenManageAccess={handleOpenManageAccess}
              />
            ))}
          </TableBody>
        </Table>
      </div>
      <div className="flex items-center justify-between pt-2">
        <div className="flex items-center space-x-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            data-testid="team-table-previous-page-button"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
          >
            <ChevronLeft className="me-1 h-4 w-4" />
            {t("teamTable.previous")}
          </Button>
          {pageNumbers(currentPage, totalPages).map((page, index) =>
            page === "gap" ? (
              <span key={`gap-${index}`} className="text-[14px]">
                ...
              </span>
            ) : (
              <Button
                key={page}
                type="button"
                variant={currentPage === page ? "default" : "ghost"}
                size="sm"
                data-testid={`team-table-page-${page}-button`}
                onClick={() => setCurrentPage(page)}
              >
                {page}
              </Button>
            )
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            data-testid="team-table-next-page-button"
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
          >
            {t("teamTable.next")}
            <ChevronRight className="ms-1 h-4 w-4" />
          </Button>
        </div>
        <span className="text-[14px] text-muted-foreground">
          {t("teamTable.showingResults", { shown: pageMembers.length, total: filteredTeamMembers.length })}
        </span>
      </div>

      <InviteUserModal
        isOpen={isInviteModalOpen}
        onOpenChange={setIsInviteModalOpen}
        organizationId={organizationId}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ["organizationData", organizationId] });
        }}
      />
      <ManageAccessDrawer
        open={isAccessDrawerOpen}
        onOpenChange={handleDrawerClose}
        user={selectedUserForAccess as TUser}
        availableProjects={[]}
        orgId={organizationId}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ["organizationData", organizationId] });
        }}
      />
    </div>
  );
}
